import { Box, Grid } from '@amzn/awsui-components-core';
import {
  Content,
  ContentAccessUser,
  ContentAudience,
  ContentHistory,
  ContentWorkflow,
  PublicationFeedback,
  InlineComment,
} from '@amzn/awscat-pattern-library-service-model';
import React, { type JSX } from 'react';
import { guideOrStrategy } from '../../api/ContentUtil';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { isUUID } from 'class-validator';
import constants from '../../common/constants';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import AppContextProps from '../../contexts/AppContextProps';
import Publication from '../../model/Publication';
import Breadcrumbs from '../common/Breadcrumbs';
import LoadingPage from '../common/LoadingPage';
import NotFound from '../common/NotFound';
import TransparentPageCanvas from '../common/TransparentPageCanvas';
import ReactRouterProps from '../utils/ReactRouterProps';
import { ReadViewTab } from '../workflow-viewer/content/ReadonlyContentData';
import PublicationContentData from './PublicationContentData';
import { ViewerType } from '../../model/Enum';
import LocalStorage from '../../common/LocalStorage';
import { getFinalizingInlineComments } from '../utils/InlineCommentUtils';
import { convertEntities } from '@amzn/awscat-editor';

interface Props extends AppContextProps, ReactRouterProps {}

interface State {
  coAuthors?: ContentAccessUser[];
  peerReviewers?: ContentAccessUser[];
  isLoading: boolean;
  publication?: Publication;
  workflowId?: number;
  workflowHistory?: ContentHistory[];
  isEditingMode: boolean;
  currentTabView?: string;
  attachmentsReadOnly: boolean;
  workflow?: ContentWorkflow;
  showSectionalComments: boolean;
  publicationFeedbacks?: PublicationFeedback[];
  inlineComments?: InlineComment[];
  isCommentingModeEnabled?: boolean;
  showInlineCommentsInEditMode?: boolean;
  showFeedbackFormInUpdateRequests?: boolean;
}

class PublicationViewer extends React.Component<Props, State> {
  protected mplServiceClient: IMplServiceClient;
  protected showComment: boolean = false;
  protected showContentControl: boolean = false;
  protected allowedEditing: boolean = false;
  protected showTechwriterMetadata: boolean = false;
  protected showSectionalComments: boolean = false;
  protected showContentAbstract: boolean = false;
  protected hideEmptySections: boolean = true;
  protected flashbarMessageBroker: FlashbarMessageBroker;
  protected parsedWorkflowId = parseInt(this.props.match.params.workflowId, constants.DECIMAL);
  // @ts-expect-error TS2322 - strict migration
  protected viewerType: ViewerType = null;
  protected setTitle: boolean = false;
  protected showLastBarRaiserEvaluation: boolean = false;
  protected showCodeValidationError: boolean = true;

  constructor(props: Props) {
    super(props);
    this.mplServiceClient = MplServiceClientFactory.create();
    this.flashbarMessageBroker = FlashbarMessageBroker.create();

    this.state = {
      attachmentsReadOnly: true,
      isEditingMode: false,
      isLoading: true,
      currentTabView: ReadViewTab.Latest,
      showSectionalComments: false,
      inlineComments: [],
      isCommentingModeEnabled: false,
      showFeedbackFormInUpdateRequests: false,
    };
  }

  public async componentDidMount() {
    const { contentId } = this.props.match.params;
    if (!isUUID(contentId)) {
      return;
    }
    // If linking to a specific feedback, switch to Update Requests tab
    const hash = window?.location?.hash?.slice(1);
    if (hash?.startsWith('feedback-')) {
      this.setState({ currentTabView: ReadViewTab.UpdateRequests });
    }
    await this.reloadData();
  }

  public async componentWillUnmount() {
    document.title = constants.DEFAULT_TAB_TITLE;
  }

  public async componentDidUpdate(prevProps: Props, prevState: State) {
    const contentId = decodeURIComponent(this.props.match.params.contentId);
    const prevContentId = decodeURIComponent(prevProps.match.params.contentId);
    if (contentId !== prevContentId) {
      await this.reloadData();
    }

    const { entities } = this.props.appContext;
    if (this.state.isLoading !== prevState.isLoading) {
      // @ts-expect-error TS2339 - strict migration
      const { contentShortName, contentName } = this.state.publication?.content.contentMetadata;
      const title: string = this.setTitle
        ? contentShortName && contentShortName !== ''
          ? contentShortName
          : contentName
        : constants.DEFAULT_TAB_TITLE;
      document.title = convertEntities(title, entities);
    }
  }

  public render() {
    const { entities } = this.props.appContext;
    const {
      coAuthors,
      isLoading,
      publication,
      workflow,
      peerReviewers,
      workflowHistory,
      isEditingMode,
      currentTabView,
      publicationFeedbacks,
      inlineComments,
      isCommentingModeEnabled,
    } = this.state;
    const { contentId } = this.props.match.params;
    if (!isUUID(contentId)) {
      return <NotFound />;
    }

    if (isLoading) {
      return <LoadingPage />;
    }

    // @ts-expect-error TS2339 - strict migration
    const { content, contentAudience, publicationDate, contentOwner, publicationState } =
      publication;

    const breadcrumbItems = [
      {
        href: '/',
        text: constants.APP_NAME_BREAD_CRUMB,
      },
      ...this.getBreadcrumbItems(),
      {
        href: '',
        text:
          convertEntities(content.contentMetadata.contentShortName, entities) ||
          convertEntities(content.contentMetadata.contentName, entities),
      },
    ];

    const breadcrumb = !isEditingMode ? <Breadcrumbs items={breadcrumbItems} /> : null;

    const publicationControls =
      !isEditingMode && currentTabView === ReadViewTab.Latest ? (
        <Grid gridDefinition={[{ colspan: 12 }]}>
          <div className="awsui-cat-flex awsui-cat-flex-end">
            <Box float="right" display="inline">
              {this.renderControls()}
            </Box>
          </div>
        </Grid>
      ) : null;

    return (
      <div className="white-background">
        {breadcrumb}
        <TransparentPageCanvas showLoadingIndicator={isLoading}>
          <PublicationContentData
            contentId={contentId}
            workflowId={this.parsedWorkflowId}
            workflow={workflow}
            coAuthors={coAuthors}
            peerReviewers={peerReviewers}
            content={content}
            contentAudience={contentAudience}
            contentOwner={contentOwner}
            isEditingAllowed={this.allowedEditing}
            showContentControls={this.showContentControl}
            onEnableEditMode={this.editModeEnabled}
            onDisableEditMode={this.editModeDisabled}
            onContentDataChanged={this.updateContent}
            onContentAudienceChanged={this.updateContentAudience}
            workflowHistory={workflowHistory}
            showTechwriterMetadata={this.showTechwriterMetadata}
            showContentAbstract={this.showContentAbstract}
            showSectionalComments={this.state.showSectionalComments}
            hideEmptySections={this.hideEmptySections}
            publicationDate={publicationDate}
            onTabViewChange={this.changeTabView}
            publicationState={publicationState}
            viewerType={this.viewerType}
            publicationFeedbacks={publicationFeedbacks}
            showLastBarRaiserEvaluation={this.showLastBarRaiserEvaluation}
            inlineComments={inlineComments}
            isCommentingModeEnabled={isCommentingModeEnabled}
            enableCommentMode={this.enableCommentMode}
            showInlineCommentsInEditMode={this.state.showInlineCommentsInEditMode}
            setShowInlineCommentsInEditMode={this.setShowInlineCommentsInEditMode}
            updateInlineCommentsLocation={this.updateInlineCommentsLocation}
            setInlineCommentsData={this.setInlineCommentsData}
            reFetchInlineComments={this.reFetchInlineComments}
            showCodeValidationError={this.showCodeValidationError}
            onReportToAuthor={this.handleReportToAuthor}
            showFeedbackFormInUpdateRequests={this.state.showFeedbackFormInUpdateRequests}
          />
          {currentTabView === ReadViewTab.Latest && (
            <div
              className={
                guideOrStrategy(content?.contentMetadata.contentType)
                  ? 'publication-feedback-multi-page'
                  : 'publication-feedback-single-page'
              }
            >
              {this.renderFeedbackForm()}
            </div>
          )}
          {publicationControls}
        </TransparentPageCanvas>
      </div>
    );
  }

  protected renderControls(): JSX.Element | null | false {
    return null;
  }

  protected renderFeedbackForm(): JSX.Element | null | false {
    return null;
  }

  protected getBreadcrumbItems(): Array<{ text: string; href: string }> {
    return [];
  }

  protected reloadData = async () => {
    return null;
  };

  protected updateContent = async (content: Content) => {
    const { publication } = { ...this.state };
    // @ts-expect-error TS18048 - strict migration
    publication.content = content;
    this.setState({
      publication,
    });
  };

  protected updateContentAudience = async (contentAudience: ContentAudience) => {
    const { publication } = { ...this.state };
    // @ts-expect-error TS18048 - strict migration
    publication.contentAudience = contentAudience;
    this.setState({
      publication,
    });
  };

  private editModeEnabled = async () => {
    this.setState({
      attachmentsReadOnly: false,
      isEditingMode: true,
    });
  };

  private editModeDisabled = async () => {
    this.setState({
      attachmentsReadOnly: true,
      isEditingMode: false,
      currentTabView: ReadViewTab.Latest,
    });
    await this.reloadData();
  };

  private changeTabView = (tabView: string) => {
    if (tabView !== ReadViewTab.UpdateRequests) {
      this.setState({ showFeedbackFormInUpdateRequests: false });
    }
    this.setState({ currentTabView: tabView });
  };

  private enableCommentMode = (enabled: boolean) => {
    this.setState({
      isCommentingModeEnabled: enabled,
    });
  };

  private setShowInlineCommentsInEditMode = (show: boolean) => {
    this.setState({ showInlineCommentsInEditMode: show });
  };

  private updateInlineCommentsLocation = (comments: InlineComment[], sectionId: string) => {
    LocalStorage.setUpdatedComments(this.props.match.params.contentId, comments, sectionId);
  };

  private setInlineCommentsData = (comments: InlineComment[]) => {
    const commentsIdSet = new Set(comments.map((comment) => comment.commentId));
    const savedComments = [...comments];
    this.state.inlineComments?.forEach((comment) => {
      if (!commentsIdSet.has(comment.commentId)) {
        savedComments.push(comment);
      }
    });
    this.setState({
      inlineComments: savedComments,
    });
  };

  private reFetchInlineComments = async () => {
    const contentId = decodeURIComponent(this.props.match.params.contentId);
    const { workflow } = this.state;
    const { inlineComments } = await this.mplServiceClient.listInlineComments(
      contentId,
      // @ts-expect-error TS18048 - strict migration
      workflow.draftWorkflowId,
    );
    this.setState({
      inlineComments: getFinalizingInlineComments(inlineComments),
    });
  };

  protected handleReportToAuthor = () => {
    this.setState({
      currentTabView: ReadViewTab.UpdateRequests,
      showFeedbackFormInUpdateRequests: true,
    });
  };
}

export default PublicationViewer;
